import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { marked } from 'marked'; // 新增：导入marked库
import { 
  getPostById, 
  incrementViewCount, 
  likePost, 
  unlikePost,
  getRelatedPosts,
  addCommentCount
} from '../../data/postsData';
import { MOCK_POST_DETAIL_DATA } from '../../api/mockData';
import './index.css';

const PostDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState([]);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [isLiked, setIsLiked] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isFollowingAuthor, setIsFollowingAuthor] = useState(false);
  const [authorFollowers, setAuthorFollowers] = useState(0);

  // 初始化文章数据和状态
  useEffect(() => {
    setLoading(true);
    
    setTimeout(() => {
      const realPost = getPostById(id);
      
      if (realPost) {
        setPost(realPost);
        
        // 获取相关文章（使用mock配置的数量限制）
        const related = getRelatedPosts(parseInt(id), MOCK_POST_DETAIL_DATA.CONFIG.RELATED_POSTS_LIMIT);
        setRelatedPosts(related);
        
        // 模拟评论数据（从mock导入）
        const mockComments = MOCK_POST_DETAIL_DATA.DEFAULT_COMMENTS;
        const adjustedComments = mockComments.slice(0, Math.min(realPost.comments || 0, MOCK_POST_DETAIL_DATA.CONFIG.MAX_COMMENTS_DISPLAY));
        setComments(adjustedComments);
        
        // 增加阅读量
        incrementViewCount(parseInt(id));
        
        // 本地存储状态（使用mock配置的key）
        const likedPostsStr = localStorage.getItem(MOCK_POST_DETAIL_DATA.CONFIG.LOCAL_STORAGE_KEYS.likedPosts) || '[]';
        const likedPosts = JSON.parse(likedPostsStr);
        setIsLiked(likedPosts.includes(realPost.id));
        
        const bookmarks = JSON.parse(localStorage.getItem(MOCK_POST_DETAIL_DATA.CONFIG.LOCAL_STORAGE_KEYS.bookmarks) || '[]');
        setIsBookmarked(bookmarks.includes(realPost.id));
        
        const following = JSON.parse(localStorage.getItem(MOCK_POST_DETAIL_DATA.CONFIG.LOCAL_STORAGE_KEYS.following) || '[]');
        setIsFollowingAuthor(following.includes(realPost.author?.id || 0));
        
        // 作者粉丝数（使用mock配置的范围）
        setAuthorFollowers(
          Math.floor(Math.random() * (MOCK_POST_DETAIL_DATA.CONFIG.AUTHOR_FOLLOWERS_MAX - MOCK_POST_DETAIL_DATA.CONFIG.AUTHOR_FOLLOWERS_MIN)) 
          + MOCK_POST_DETAIL_DATA.CONFIG.AUTHOR_FOLLOWERS_MIN
        );
      }
      
      setLoading(false);
    }, 800);

    // 修复原代码：清理函数缩进错误
    const handleDataChange = (event) => {
      if (event.detail?.postId === parseInt(id)) {
        const updatedPost = getPostById(id);
        if (updatedPost) {
          setPost(updatedPost);
        }
      }
    };

    window.addEventListener('blogDataChanged', handleDataChange);

    // 正确的清理函数
    return () => {
      window.removeEventListener('blogDataChanged', handleDataChange);
    };
  }, [id]);

  // 新增：解析Markdown内容
  const parseMarkdown = (content) => {
    if (!content) return '';
    // 配置marked解析规则
    marked.setOptions({
      breaks: true,
      gfm: true,
    });
    return marked.parse(content);
  };

  // 点赞功能
  const handleLike = () => {
    if (!post) return;
    
    const likedPosts = JSON.parse(localStorage.getItem(MOCK_POST_DETAIL_DATA.CONFIG.LOCAL_STORAGE_KEYS.likedPosts) || '[]');
    const alreadyLiked = likedPosts.includes(post.id);
    
    if (alreadyLiked) {
      const newLikeCount = unlikePost(parseInt(id));
      setPost(prevPost => ({ ...prevPost, likes: newLikeCount }));
      
      const newLikedPosts = likedPosts.filter(postId => postId !== post.id);
      localStorage.setItem(MOCK_POST_DETAIL_DATA.CONFIG.LOCAL_STORAGE_KEYS.likedPosts, JSON.stringify(newLikedPosts));
      setIsLiked(false);
    } else {
      const newLikeCount = likePost(parseInt(id));
      setPost(prevPost => ({ ...prevPost, likes: newLikeCount }));
      
      likedPosts.push(post.id);
      localStorage.setItem(MOCK_POST_DETAIL_DATA.CONFIG.LOCAL_STORAGE_KEYS.likedPosts, JSON.stringify(likedPosts));
      setIsLiked(true);
    }
  };

  // 收藏功能（修复变量未定义错误）
  const handleBookmark = () => {
    if (!post) return;
    
    // 1. 先获取当前收藏列表
    const bookmarks = JSON.parse(localStorage.getItem(MOCK_POST_DETAIL_DATA.CONFIG.LOCAL_STORAGE_KEYS.bookmarks) || '[]');
    
    if (isBookmarked) {
      // 取消收藏：过滤掉当前文章ID，生成新列表
      const newBookmarks = bookmarks.filter(bookmarkId => bookmarkId !== post.id); // 修正：正确声明newBookmarks
      localStorage.setItem(MOCK_POST_DETAIL_DATA.CONFIG.LOCAL_STORAGE_KEYS.bookmarks, JSON.stringify(newBookmarks));
      setIsBookmarked(false);
      alert(MOCK_POST_DETAIL_DATA.TEXT.BOOKMARK.remove);
    } else {
      // 添加收藏：直接push后存回
      bookmarks.push(post.id);
      localStorage.setItem(MOCK_POST_DETAIL_DATA.CONFIG.LOCAL_STORAGE_KEYS.bookmarks, JSON.stringify(bookmarks));
      setIsBookmarked(true);
      alert(MOCK_POST_DETAIL_DATA.TEXT.BOOKMARK.add);
    }
  };

  // 关注作者功能
  const handleFollowAuthor = () => {
    if (!post?.author) return;
    
    const following = JSON.parse(localStorage.getItem(MOCK_POST_DETAIL_DATA.CONFIG.LOCAL_STORAGE_KEYS.following) || '[]');
    const authorId = post.author.id || 0;
    const authorName = post.author.name;
    
    if (isFollowingAuthor) {
      const newFollowing = following.filter(id => id !== authorId);
      localStorage.setItem(MOCK_POST_DETAIL_DATA.CONFIG.LOCAL_STORAGE_KEYS.following, JSON.stringify(newFollowing));
      setIsFollowingAuthor(false);
      setAuthorFollowers(prev => Math.max(MOCK_POST_DETAIL_DATA.CONFIG.AUTHOR_FOLLOWERS_MIN, prev - 1));
      alert(MOCK_POST_DETAIL_DATA.TEXT.FOLLOW.remove(authorName));
    } else {
      following.push(authorId);
      localStorage.setItem(MOCK_POST_DETAIL_DATA.CONFIG.LOCAL_STORAGE_KEYS.following, JSON.stringify(following));
      setIsFollowingAuthor(true);
      setAuthorFollowers(prev => prev + 1);
      alert(MOCK_POST_DETAIL_DATA.TEXT.FOLLOW.add(authorName));
    }
  };

  // 分享功能
  const handleShare = (platform = 'copy') => {
    const articleUrl = window.location.href;
    const articleTitle = post?.title || '精彩文章';
    switch (platform) {
      case 'twitter':
        const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(articleTitle)}&url=${encodeURIComponent(articleUrl)}`;
        window.open(twitterUrl, '_blank', 'width=550,height=420');
        break;
        
      case 'weibo':
        const weiboUrl = `http://service.weibo.com/share/share.php?url=${encodeURIComponent(articleUrl)}&title=${encodeURIComponent(articleTitle)}&pic=${encodeURIComponent('')}`;
        window.open(weiboUrl, '_blank', 'width=550,height=420');
        break;
        
      case 'copy':
      default:
        navigator.clipboard.writeText(articleUrl)
          .then(() => alert(MOCK_POST_DETAIL_DATA.TEXT.SHARE.copySuccess))
          .catch(() => {
            const textArea = document.createElement('textarea');
            textArea.value = articleUrl;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            alert(MOCK_POST_DETAIL_DATA.TEXT.SHARE.copySuccess);
          });
        break;
    }
  };

  // 发表评论功能
  const handleCommentSubmit = (e) => {
    e.preventDefault();
    if (comment.trim() && post) {
      const newComment = {
        id: comments.length + 1,
        author: '当前用户',
        avatar: MOCK_POST_DETAIL_DATA.ICONS.avatar,
        content: comment,
        date: format(new Date(), 'yyyy-MM-dd HH:mm'),
        likes: 0
      };
      
      setComments([newComment, ...comments]);
      setComment('');
      
      const newCommentCount = addCommentCount(parseInt(id));
      setPost({ ...post, comments: newCommentCount });
      
      alert(MOCK_POST_DETAIL_DATA.TEXT.COMMENT_SUCCESS);
    }
  };

  // 评论点赞功能
  const handleCommentLike = (commentId) => {
    setComments(comments.map(comment => {
      if (comment.id === commentId) {
        return { ...comment, likes: comment.likes + 1 };
      }
      return comment;
    }));
  };

  // 加载状态
  if (loading) {
    return (
      <div className="post-detail-loading">
        <div className="loading-content">
          <span className="loading-dog">{MOCK_POST_DETAIL_DATA.TEXT.LOADING.dogIcon}</span>
          <p>{MOCK_POST_DETAIL_DATA.TEXT.LOADING.main}</p>
          <div className="paw-trail">
            {MOCK_POST_DETAIL_DATA.TEXT.LOADING.pawTrail.map((paw, idx) => (
              <span key={idx} className="paw">{paw}</span>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // 文章未找到
  if (!post) {
    return (
      <div className="post-not-found">
        <h2>{MOCK_POST_DETAIL_DATA.TEXT.POST_NOT_FOUND.title}</h2>
        <p>{MOCK_POST_DETAIL_DATA.TEXT.POST_NOT_FOUND.subtext}</p>
        <button className="btn btn-primary" onClick={() => navigate('/')}>
          {MOCK_POST_DETAIL_DATA.TEXT.POST_NOT_FOUND.btnText}
        </button>
      </div>
    );
  }

  return (
    <div className="post-detail-page fade-in">
      {/* 文章头部 */}
      <div className="post-header-section">
        <div className="breadcrumb">
          <button className="back-btn" onClick={() => navigate(-1)}>
            {MOCK_POST_DETAIL_DATA.TEXT.BACK_BTN_TEXT}
          </button>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-category">{post.category}</span>
        </div>
        
        <h1 className="post-title">{post.title}</h1>
        
        <div className="post-meta-info">
          <div className="author-info">
            <div className="author-avatar">
              <span className="avatar-icon">{post.author?.avatar || MOCK_POST_DETAIL_DATA.ICONS.avatar}</span>
            </div>
            <div className="author-details">
              <span className="author-name">{post.author?.name || MOCK_POST_DETAIL_DATA.TEXT.UNKNOWN_AUTHOR}</span>
              <span className="author-role">{post.author?.role || ''}</span>
            </div>
          </div>
          
          <div className="post-stats">
            <span className="stat-item">
              <span className="stat-icon">{MOCK_POST_DETAIL_DATA.ICONS.date}</span>
              {format(new Date(post.date || post.createdAt), 'yyyy年MM月dd日')}
            </span>
            <span className="stat-item">
              <span className="stat-icon">{MOCK_POST_DETAIL_DATA.ICONS.time}</span>
              {post.readTime || MOCK_POST_DETAIL_DATA.CONFIG.DEFAULT_READ_TIME}分钟阅读
            </span>
            <span className="stat-item">
              <span className="stat-icon">{MOCK_POST_DETAIL_DATA.ICONS.view}</span>
              {post.views || 0}次阅读
            </span>
          </div>
        </div>

        {/* 标签 */}
        <div className="post-tags">
          {post.tags && post.tags.map((tag, index) => (
            <span key={index} className="post-tag">
              #{tag}
            </span>
          ))}
        </div>
      </div>

      {/* 文章内容 */}
      <div className="post-content-wrapper">
        <div className="post-content">
          {/* 核心修改：使用parseMarkdown解析文章内容 */}
          <div 
            className="post-body"
            dangerouslySetInnerHTML={{ __html: parseMarkdown(post.content || MOCK_POST_DETAIL_DATA.TEXT.EMPTY_CONTENT) }}
          />
          
          {/* 文章底部交互 */}
          <div className="post-interactions">
            <div className="interaction-buttons">
              <button 
                className={`interaction-btn like-btn ${isLiked ? 'liked' : ''}`} 
                onClick={handleLike}
              >
                <span className="btn-icon">{isLiked ? MOCK_POST_DETAIL_DATA.ICONS.like.active : MOCK_POST_DETAIL_DATA.ICONS.like.inactive}</span>
                <span className="btn-text">喜欢 ({post.likes || 0})</span>
              </button>
              <button className="interaction-btn comment-btn">
                <span className="btn-icon">{MOCK_POST_DETAIL_DATA.ICONS.comment}</span>
                <span className="btn-text">评论 ({post.comments || 0})</span>
              </button>
              <button 
                className="interaction-btn share-btn" 
                onClick={() => handleShare('copy')}
              >
                <span className="btn-icon">{MOCK_POST_DETAIL_DATA.ICONS.share}</span>
                <span className="btn-text">分享</span>
              </button>
              <button 
                className={`interaction-btn bookmark-btn ${isBookmarked ? 'bookmarked' : ''}`}
                onClick={handleBookmark}
              >
                <span className="btn-icon">{isBookmarked ? MOCK_POST_DETAIL_DATA.ICONS.bookmark.active : MOCK_POST_DETAIL_DATA.ICONS.bookmark.inactive}</span>
                <span className="btn-text">{isBookmarked ? '已收藏' : '收藏'}</span>
              </button>
            </div>
          </div>

          {/* 作者信息卡片 */}
          {post.author && (
            <div className="author-card">
              <div className="author-card-header">
                <div className="author-card-avatar">
                  <span className="avatar-icon">{post.author.avatar}</span>
                </div>
                <div className="author-card-info">
                  <div>
                    <h4>{post.author.name}</h4>
                    <p className="author-card-role">{post.author.role}</p>
                  </div>
                  <div className="author-followers">
                    <span className="followers-count">{authorFollowers.toLocaleString()}</span>
                    <span className="followers-label">{MOCK_POST_DETAIL_DATA.TEXT.AUTHOR_FOLLOWERS_LABEL}</span>
                  </div>
                </div>
              </div>
              <p className="author-bio">{post.author.bio}</p>
              <button 
                className={`btn follow-author-btn ${isFollowingAuthor ? 'following' : ''}`}
                onClick={handleFollowAuthor}
              >
                <span className="btn-icon">
                  {isFollowingAuthor ? MOCK_POST_DETAIL_DATA.ICONS.follow.active : MOCK_POST_DETAIL_DATA.ICONS.follow.inactive}
                </span>
                {isFollowingAuthor ? '已关注' : '关注作者'}
              </button>
            </div>
          )}

          {/* 评论区域 */}
          <div className="comments-section">
            <h3 className="comments-title">
              <span className="comment-icon">{MOCK_POST_DETAIL_DATA.ICONS.comment}</span>
              {MOCK_POST_DETAIL_DATA.TEXT.COMMENTS_TITLE} ({post?.comments || 0})
            </h3>
            
            <form className="comment-form" onSubmit={handleCommentSubmit}>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={MOCK_POST_DETAIL_DATA.TEXT.COMMENT_PLACEHOLDER}
                className="comment-input"
                rows="4"
              />
              <div className="comment-form-actions">
                <button type="submit" className="btn btn-primary">
                  {MOCK_POST_DETAIL_DATA.TEXT.COMMENT_SUBMIT_BTN}
                </button>
              </div>
            </form>

            <div className="comments-list">
              {comments.map((commentItem) => (
                <div key={commentItem.id} className="comment-item">
                  <div className="comment-header">
                    <div className="comment-author">
                      <span className="comment-avatar">{commentItem.avatar}</span>
                      <span className="comment-author-name">{commentItem.author}</span>
                      <span className="comment-date">{commentItem.date}</span>
                    </div>
                    <button 
                      className="comment-like-btn"
                      onClick={() => handleCommentLike(commentItem.id)}
                    >
                      {MOCK_POST_DETAIL_DATA.ICONS.commentLike} {commentItem.likes}
                    </button>
                  </div>
                  <p className="comment-content">{commentItem.content}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 侧边栏 */}
        <div className="post-sidebar">
          {/* 目录 */}
          <div className="sidebar-section table-of-contents">
            <h4>{MOCK_POST_DETAIL_DATA.TEXT.DIRECTORY.title}</h4>
            <ul className="toc-list">
              {MOCK_POST_DETAIL_DATA.TEXT.DIRECTORY.items.map((item) => (
                <li key={item.id}>
                  <a href={`#${item.id}`}>{item.text}</a>
                </li>
              ))}
            </ul>
          </div>

          {/* 相关文章 */}
          <div className="sidebar-section related-posts">
            <h4>{MOCK_POST_DETAIL_DATA.TEXT.RELATED_POSTS_TITLE}</h4>
            {relatedPosts.length > 0 ? (
              relatedPosts.map((relatedPost) => (
                <div 
                  key={relatedPost.id} 
                  className="related-post"
                  onClick={() => navigate(`/post/${relatedPost.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <h5>{relatedPost.title}</h5>
                  <p>{relatedPost.excerpt}</p>
                  <div className="related-post-meta">
                    <span>{relatedPost.category}</span>
                    <span>·</span>
                    <span>{relatedPost.readTime || MOCK_POST_DETAIL_DATA.CONFIG.DEFAULT_READ_TIME}分钟阅读</span>
                  </div>
                </div>
              ))
            ) : (
              <p className="no-related-posts">{MOCK_POST_DETAIL_DATA.TEXT.EMPTY_RELATED_POSTS}</p>
            )}
          </div>

          {/* 分享卡片 */}
          <div className="sidebar-section share-card">
            <h4>{MOCK_POST_DETAIL_DATA.TEXT.SHARE_CARD_TITLE}</h4>
            <div className="share-buttons">
              <button 
                className="share-btn twitter"
                onClick={() => handleShare('twitter')}
              >
                <span>{MOCK_POST_DETAIL_DATA.ICONS.twitter}</span> {MOCK_POST_DETAIL_DATA.TEXT.SHARE.twitter}
              </button>
              <button 
                className="share-btn weibo"
                onClick={() => handleShare('weibo')}
              >
                <span>{MOCK_POST_DETAIL_DATA.ICONS.weibo}</span> {MOCK_POST_DETAIL_DATA.TEXT.SHARE.weibo}
              </button>
              <button 
                className="share-btn copy" 
                onClick={() => handleShare('copy')}
              >
                <span>{MOCK_POST_DETAIL_DATA.ICONS.copy}</span> {MOCK_POST_DETAIL_DATA.TEXT.SHARE.copy}
              </button>
            </div>
            <p className="share-tip">{MOCK_POST_DETAIL_DATA.TEXT.SHARE.tip}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostDetail;